// Hjelparar for Basar-modulen (loddsal). Berre UI-logikk – ingen omsetjing av arrangørinnhald.
import { useCallback, useEffect, useState } from "react";
import { tr, withLang } from "./i18n";
import { api, formatLocal } from "./api";

export { nok } from "./shop";

export const TICKET_PAGE_SIZE = 100;

export function basarUrl(basarId) {
  return `/basar/${basarId}`;
}

export function basarOrderUrl(orderId) {
  return `/basar/ordre/${orderId}`;
}

// Delingslenke via /api/share/basar (Open Graph med delingskortet) – same mønster som arrangement (eventShareUrl)
export function basarShareUrl(basarId) {
  return withLang(`${process.env.REACT_APP_BACKEND_URL}/api/share/basar/${basarId}`);
}

// Offentlege bilete-/PDF-adresser (treng ikkje innlogging; berre for betalte ordrar)
export function basarOrderQrUrl(orderId) {
  return `${process.env.REACT_APP_BACKEND_URL}/api/basar/orders/${orderId}/qr.png`;
}

export function basarOrderReceiptUrl(orderId) {
  return withLang(`${process.env.REACT_APP_BACKEND_URL}/api/basar/orders/${orderId}/receipt.pdf`);
}

// Kontrollsida i dashbordet som QR-en på kvitteringa peikar på
export function dashboardBasarCheckUrl(basarId, orderId = "") {
  return `/dashboard/basar/${basarId}/kontroll${orderId ? `/${orderId}` : ""}`;
}

// Sideindeks (0-basert) for eit loddnummer i det paginerte rutenettet
export function pageIndexFor(number, seriesFrom, pageSize = TICKET_PAGE_SIZE) {
  return Math.max(0, Math.floor((Number(number) - Number(seriesFrom)) / pageSize));
}

// «3, 4, 5, 12, 13» → «3–5, 12–13» (kompakt visning av mange nummer)
export function compressNumbers(numbers) {
  const nums = [...new Set((numbers || []).map(Number))].sort((a, b) => a - b);
  const out = [];
  let i = 0;
  while (i < nums.length) {
    let j = i;
    while (j + 1 < nums.length && nums[j + 1] === nums[j] + 1) j += 1;
    out.push(j - i >= 2 ? `${nums[i]}–${nums[j]}` : j === i ? `${nums[i]}` : `${nums[i]}, ${nums[j]}`);
    i = j + 1;
  }
  return out.join(", ");
}

// Basar-status → etikett + tokenbaserte klassar (open=success, closed=secondary, drawn=warning)
export function basarStatusMeta(basar) {
  const s = basar?.status;
  if (s === "drawn") return { label: tr("Trekningen er klar"), className: "bg-[hsl(var(--warning))] text-[hsl(var(--warning-foreground))] hover:bg-[hsl(var(--warning))]", key: "drawn" };
  if (s === "closed") return { label: tr("Salget er stengt"), className: "bg-secondary text-secondary-foreground border border-border hover:bg-secondary", key: "closed" };
  if (s === "open" && basar?.in_sale_period === false) return { label: tr("Åpner snart"), className: "bg-[hsl(var(--info))] text-[hsl(var(--info-foreground))] hover:bg-[hsl(var(--info))]", key: "upcoming" };
  if (s === "open") return { label: tr("Loddsalg pågår"), className: "bg-[hsl(var(--success))] text-[hsl(var(--success-foreground))] hover:bg-[hsl(var(--success))]", key: "open" };
  if (s === "draft") return { label: tr("Kladd"), className: "bg-secondary text-secondary-foreground border border-border hover:bg-secondary", key: "draft" };
  return { label: s || "", className: "bg-secondary text-secondary-foreground", key: s || "" };
}

// Loddordre-status (kjøpar og arrangør)
export function basarOrderStatusMeta(order) {
  const s = order?.status;
  if (s === "pending_payment" || s === "processing") return { label: tr("Venter på Vipps"), className: "bg-[hsl(var(--info))] text-[hsl(var(--info-foreground))] hover:bg-[hsl(var(--info))]", key: "pending" };
  if (s === "paid") return { label: tr("Betalt"), className: "bg-[hsl(var(--success))] text-[hsl(var(--success-foreground))] hover:bg-[hsl(var(--success))]", key: "paid" };
  if (s === "cancelled") return { label: tr("Kansellert"), className: "bg-[hsl(var(--danger))] text-[hsl(var(--danger-foreground))] hover:bg-[hsl(var(--danger))]", key: "cancelled" };
  if (s === "refunded" || s === "refunding") return { label: tr("Refundert"), className: "bg-[hsl(var(--danger))] text-[hsl(var(--danger-foreground))] hover:bg-[hsl(var(--danger))]", key: "refunded" };
  return { label: s || "", className: "bg-secondary text-secondary-foreground", key: s || "" };
}

export function basarPaymentLabel(order) {
  const m = order?.payment_method;
  if (m === "cash") return tr("Kontant");
  if (m === "vipps_manual") return tr("Vipps ved døren");
  if (m === "other") return tr("Annen betaling");
  return tr("Vipps");
}

// ---------- Gåver («Gi premie», Phase 78) ----------

export function basarGiftUrl(giftId) {
  return `/basar/gave/${giftId}`;
}

export function giftKindLabel(kind) {
  return kind === "money" ? tr("Pengegave") : tr("Tinggave");
}

export function giftPaymentLabel(gift) {
  const m = gift?.payment_method;
  if (gift?.kind !== "money" || !m) return "";
  if (m === "cash") return tr("Kontant");
  if (m === "vipps_manual") return tr("Vipps ved døren");
  if (m === "bank") return tr("Bankoverføring");
  if (m === "other") return tr("Annen betaling");
  return tr("Vipps");
}

// Gåve-status (givar og arrangør): registered/paid=success, pending=info, cancelled=danger
export function basarGiftStatusMeta(gift) {
  const s = gift?.status;
  if (s === "pending_payment" || s === "processing") return { label: tr("Venter på Vipps"), className: "bg-[hsl(var(--info))] text-[hsl(var(--info-foreground))] hover:bg-[hsl(var(--info))]", key: "pending" };
  if (s === "paid") return { label: tr("Betalt"), className: "bg-[hsl(var(--success))] text-[hsl(var(--success-foreground))] hover:bg-[hsl(var(--success))]", key: "paid" };
  if (s === "registered") return { label: tr("Registrert"), className: "bg-[hsl(var(--success))] text-[hsl(var(--success-foreground))] hover:bg-[hsl(var(--success))]", key: "registered" };
  if (s === "cancelled") return { label: tr("Kansellert"), className: "bg-[hsl(var(--danger))] text-[hsl(var(--danger-foreground))] hover:bg-[hsl(var(--danger))]", key: "cancelled" };
  return { label: s || "", className: "bg-secondary text-secondary-foreground", key: s || "" };
}

// «laurdag 21. november 2026 kl. 18:00» – trekningstidspunkt på basarside/plakat-felt
export function drawDateTime(iso) {
  return formatLocal(iso, { weekday: "long", day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

export function soldPercent(stats) {
  const total = Number(stats?.total || 0);
  if (!total) return 0;
  return Math.min(100, Math.round((Number(stats?.sold || 0) / total) * 100));
}

// «tor. 27. nov. kl. 12:21» – kort dato/tid for salsslutt o.l.
export function shortDateTime(iso) {
  return formatLocal(iso, { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

export function shortTime(iso) {
  return formatLocal(iso, { hour: "2-digit", minute: "2-digit" });
}

// ---------- Dashbord (arrangør) ----------

export function dashboardBasarUrl(basarId, sub = "") {
  return `/dashboard/basar/${basarId}${sub ? `/${sub}` : ""}`;
}

// ISO (UTC) → verdi for <input type="datetime-local"> i lokal tid, og tilbake
export function toLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function fromLocalInput(local) {
  if (!local) return "";
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? "" : d.toISOString();
}

/** Hentar arrangøren sin basar (med metrikkar) + Vipps/e-post-flagg. `reload()` hentar på nytt utan å blanke visninga. */
export function useMyBasar(basarId) {
  const [state, setState] = useState({ basar: null, vippsAvailable: false, emailEnabled: false, loading: true, error: null });
  const load = useCallback(
    (silent = false) => {
      if (!basarId) return Promise.resolve();
      if (!silent) setState((s) => ({ ...s, loading: true, error: null }));
      return api
        .get(`/my/basar/${basarId}`)
        .then((res) => setState({ basar: res.data.basar, vippsAvailable: !!res.data.vipps_available, emailEnabled: !!res.data.email_enabled, loading: false, error: null }))
        .catch((err) => setState((s) => ({ ...s, loading: false, error: err?.response?.status === 404 ? "notfound" : "error" })));
    },
    [basarId]
  );
  useEffect(() => {
    load();
  }, [load]);
  const setBasar = useCallback((basar) => setState((s) => ({ ...s, basar })), []);
  return { ...state, reload: () => load(true), setBasar };
}
